import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Shield, BarChart3, Gauge, Layers, FileCode, Download, Copy, CheckCircle,
  XCircle, AlertTriangle, Server, Activity, Zap, Lock, DollarSign, GitBranch,
  ChevronDown, ChevronRight, ExternalLink, Box,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Tab = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    className={`tab-trigger ${active ? 'active' : ''}`}
    onClick={onClick}
    data-testid={testId}
    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
  >
    <Icon size={14} /> {label}
  </button>
);

const MetricCard = ({ value, label, color, sub }) => (
  <div style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', textAlign: 'center', minWidth: 0 }}>
    <div style={{ fontSize: '1.375rem', fontWeight: 700, color: color || 'inherit', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.125rem' }}>{label}</div>
    {sub && <div style={{ fontSize: '0.5625rem', color: 'hsl(var(--muted-foreground))' }}>{sub}</div>}
  </div>
);

const PolicyBadge = ({ status }) => {
  const styles = {
    pass: { bg: '#10b98120', color: '#10b981', label: 'PASS' },
    fail: { bg: '#ef444420', color: '#ef4444', label: 'FAIL' },
    warn: { bg: '#f59e0b20', color: '#f59e0b', label: 'WARN' },
  };
  const s = styles[status] || styles.warn;
  return (
    <span style={{ padding: '0.125rem 0.5rem', borderRadius: '999px', fontSize: '0.625rem', fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>
  );
};

export default function DevOpsPage() {
  const { projectId } = useParams();
  const [tab, setTab] = useState('overview');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId || '');
  const [dashboard, setDashboard] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [tokenCosts, setTokenCosts] = useState(null);
  const [cicd, setCicd] = useState(null);
  const [container, setContainer] = useState(null);
  const [costEstimate, setCostEstimate] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cicdLoading, setCicdLoading] = useState(false);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [projRes, dashRes] = await Promise.all([
          fetch(`${API_URL}/api/devops/projects`, { credentials: 'include' }).then(r => r.json()),
          fetch(`${API_URL}/api/devops/dashboard`, { credentials: 'include' }).then(r => r.json()),
        ]);
        setProjects(projRes.projects || []);
        setDashboard(dashRes.dashboard || null);
        if (!selectedProject && projRes.projects?.length > 0) {
          setSelectedProject(projRes.projects[0].id);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedProject) return;
    const fetchProjectData = async () => {
      const base = `${API_URL}/api/devops/projects/${selectedProject}`;
      const opts = { credentials: 'include' };
      const [confRes, tokenRes, containerRes, costRes, compRes, polRes] = await Promise.all([
        fetch(`${base}/confidence`, opts).then(r => r.json()).catch(() => ({})),
        fetch(`${base}/token-costs`, opts).then(r => r.json()).catch(() => ({})),
        fetch(`${base}/container-intel`, opts).then(r => r.json()).catch(() => ({})),
        fetch(`${base}/cost-estimate`, opts).then(r => r.json()).catch(() => ({})),
        fetch(`${base}/compliance`, opts).then(r => r.json()).catch(() => ({})),
        fetch(`${base}/policies`, opts).then(r => r.json()).catch(() => ({})),
      ]);
      setConfidence(confRes);
      setTokenCosts(tokenRes);
      setContainer(containerRes.analysis);
      setCostEstimate(costRes.estimate);
      setCompliance(compRes.report);
      setPolicies(polRes);
    };
    fetchProjectData();
  }, [selectedProject]);

  const generateCICD = async () => {
    if (!selectedProject) return;
    setCicdLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/devops/projects/${selectedProject}/generate-cicd`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      setCicd(data);
      setTab('cicd');
    } catch (err) { console.error(err); }
    setCicdLoading(false);
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const proj = projects.find(p => p.id === selectedProject) || {};

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div data-testid="devops-page" style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <Link to="/history" style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.75rem' }}>
        <ArrowLeft size={14} /> Back
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 700, letterSpacing: '-0.02em' }}>
          <Server size={22} style={{ display: 'inline', verticalAlign: '-3px', marginRight: '0.5rem', color: 'hsl(var(--accent))' }} />
          DevOps Intelligence
        </h1>
        {projects.length > 0 && (
          <select
            value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}
            className="input"
            style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem', maxWidth: '220px' }}
            data-testid="devops-project-selector"
          >
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      {/* Org Dashboard Summary */}
      {dashboard && (
        <div className="card" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }} data-testid="org-dashboard">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '0.5rem' }}>
            <MetricCard value={dashboard.total_projects} label="Projects" />
            <MetricCard value={`${dashboard.avg_confidence}%`} label="Avg Confidence" color={dashboard.avg_confidence >= 80 ? '#10b981' : '#f59e0b'} />
            <MetricCard value={`${dashboard.avg_security_score}%`} label="Avg Security" color={dashboard.avg_security_score >= 80 ? '#10b981' : '#f59e0b'} />
            <MetricCard value={`${dashboard.avg_test_coverage}%`} label="Avg Coverage" />
            <MetricCard value={`${dashboard.pipeline_success_rate}%`} label="Pipeline Success" color="#10b981" />
            <MetricCard value={`$${dashboard.total_cost}`} label="Total Cost" />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tab-list" style={{ marginBottom: '1rem' }}>
        <Tab active={tab === 'overview'} onClick={() => setTab('overview')} icon={Gauge} label="Confidence" testId="devops-tab-overview" />
        <Tab active={tab === 'tokens'} onClick={() => setTab('tokens')} icon={Zap} label="Token Costs" testId="devops-tab-tokens" />
        <Tab active={tab === 'cicd'} onClick={() => { setTab('cicd'); if (!cicd) generateCICD(); }} icon={GitBranch} label="CI/CD" testId="devops-tab-cicd" />
        <Tab active={tab === 'container'} onClick={() => setTab('container')} icon={Box} label="Container" testId="devops-tab-container" />
        <Tab active={tab === 'cost'} onClick={() => setTab('cost')} icon={DollarSign} label="Infra Cost" testId="devops-tab-cost" />
        <Tab active={tab === 'compliance'} onClick={() => setTab('compliance')} icon={Shield} label="Compliance" testId="devops-tab-compliance" />
        <Tab active={tab === 'policies'} onClick={() => setTab('policies')} icon={Lock} label="Policies" testId="devops-tab-policies" />
      </div>

      {/* Deployment Confidence */}
      {tab === 'overview' && confidence && (
        <div data-testid="confidence-panel">
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '96px', height: '96px', borderRadius: '50%',
                  border: `5px solid ${confidence.confidence >= 80 ? '#10b981' : confidence.confidence >= 60 ? '#f59e0b' : '#ef4444'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 800, color: confidence.confidence >= 80 ? '#10b981' : confidence.confidence >= 60 ? '#f59e0b' : '#ef4444' }}>
                    {confidence.confidence}%
                  </div>
                </div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '0.5rem' }}>Deployment Confidence</div>
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>Breakdown</h3>
                {confidence.breakdown && Object.entries(confidence.breakdown).map(([key, val]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', width: '120px', textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
                    <div style={{ flex: 1, height: '6px', background: 'hsl(var(--secondary))', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ width: `${val.score}%`, height: '100%', background: val.score >= 80 ? '#10b981' : val.score >= 60 ? '#f59e0b' : '#ef4444', borderRadius: '3px' }} />
                    </div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, width: '40px', textAlign: 'right' }}>{val.score}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Token Costs */}
      {tab === 'tokens' && (
        <div data-testid="token-costs-panel">
          {tokenCosts?.totals?.total_tokens > 0 ? (
            <>
              <div className="card" style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Token Cost Summary</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.5rem' }}>
                  <MetricCard value={tokenCosts.totals.total_tokens.toLocaleString()} label="Total Tokens" />
                  <MetricCard value={`$${tokenCosts.totals.total_cost}`} label="Total Cost" color="#10b981" />
                  <MetricCard value={tokenCosts.totals.avg_tokens_per_build.toLocaleString()} label="Avg/Build" />
                  <MetricCard value={`$${tokenCosts.totals.avg_cost_per_build}`} label="Avg Cost/Build" />
                </div>
              </div>
              {tokenCosts.builds?.map((build, bi) => (
                <div className="card" key={bi} style={{ marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{build.version}</span>
                    <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{build.total_tokens.toLocaleString()} tokens — ${build.total_cost}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {build.stages.map((s, si) => (
                      <div key={si} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.5rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.75rem' }}>
                        <span style={{ fontWeight: 600, width: '70px' }}>{s.agent}</span>
                        <div style={{ flex: 1, height: '4px', background: 'hsl(var(--border))', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(100, (s.total_tokens / Math.max(build.total_tokens, 1)) * 100)}%`, height: '100%', background: 'hsl(var(--accent))', borderRadius: '2px' }} />
                        </div>
                        <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.6875rem', width: '80px', textAlign: 'right' }}>{s.total_tokens.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <Zap size={32} style={{ color: 'hsl(var(--muted-foreground))', margin: '0 auto 0.75rem', opacity: 0.4 }} />
              <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>No token usage data yet</p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Token costs will appear after a successful build completes.</p>
            </div>
          )}
        </div>
      )}

      {/* CI/CD Generator */}
      {tab === 'cicd' && (
        <div data-testid="cicd-panel">
          {cicdLoading ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem' }}><div className="spinner" style={{ margin: '0 auto' }} /><p style={{ marginTop: '0.5rem', fontSize: '0.8125rem' }}>Generating CI/CD artifacts...</p></div>
          ) : cicd ? (
            <>
              {cicd.risk_analysis && (
                <div className="card" style={{ marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Pipeline Risk Analysis</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem' }}>
                    {Object.entries(cicd.risk_analysis).map(([k, v]) => (
                      <div key={k} style={{ padding: '0.5rem 0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.75rem' }}>
                        <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.625rem', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</div>
                        <div style={{ fontWeight: 600, color: v === true ? '#ef4444' : v === 'Low' ? '#10b981' : v === 'Medium' ? '#f59e0b' : 'inherit' }}>
                          {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {cicd.artifacts && Object.entries(cicd.artifacts).map(([key, art]) => (
                <div className="card" key={key} style={{ marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <FileCode size={14} /> {art.filename}
                    </h4>
                    <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem', fontSize: '0.6875rem' }}
                      onClick={() => copyText(art.content, key)} data-testid={`copy-${key}`}>
                      {copied === key ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                  <pre style={{ background: 'hsl(var(--background))', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.6875rem', overflow: 'auto', maxHeight: '300px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {art.content}
                  </pre>
                </div>
              ))}
            </>
          ) : <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
              <GitBranch size={32} style={{ color: 'hsl(var(--muted-foreground))', margin: '0 auto 0.75rem', opacity: 0.4 }} />
              <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>Generating CI/CD artifacts...</p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>If this takes too long, the project may not have build artifacts yet.</p>
            </div>}
        </div>
      )}

      {/* Container Intelligence */}
      {tab === 'container' && (
        <div data-testid="container-panel">
          {container ? (
            <>
              <div className="card" style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Container Analysis</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.5rem' }}>
                  <MetricCard value={`${container.estimated_image_size_mb}MB`} label="Est. Image Size" color={container.estimated_image_size_mb > 300 ? '#f59e0b' : '#10b981'} />
                  <MetricCard value={container.base_image} label="Base Image" />
                  <MetricCard value={container.dependency_count} label="Dependencies" />
                  <MetricCard value={`${container.total_code_size_kb}KB`} label="Code Size" />
                  <MetricCard value={container.layer_count} label="Layers" />
                  <MetricCard value={container.vulnerabilities} label="Vulns" color="#10b981" />
                </div>
              </div>
              {container.optimizations?.length > 0 && (
                <div className="card">
                  <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Optimization Suggestions</h3>
                  {container.optimizations.map((opt, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', marginBottom: '0.375rem', fontSize: '0.75rem' }}>
                      <span style={{
                        padding: '0.125rem 0.375rem', borderRadius: '999px', fontSize: '0.5625rem', fontWeight: 700,
                        background: opt.impact === 'High' ? '#ef444420' : opt.impact === 'Medium' ? '#f59e0b20' : '#6b728020',
                        color: opt.impact === 'High' ? '#ef4444' : opt.impact === 'Medium' ? '#f59e0b' : '#6b7280',
                      }}>{opt.impact}</span>
                      <span>{opt.suggestion}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <Box size={32} style={{ color: 'hsl(var(--muted-foreground))', margin: '0 auto 0.75rem', opacity: 0.4 }} />
              <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>No container analysis available</p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Container intelligence will appear after a successful build.</p>
            </div>}
        </div>
      )}

      {/* Infra Cost Estimate */}
      {tab === 'cost' && (
        <div data-testid="cost-panel">
          {costEstimate ? (
            <>
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: 600 }}>Projected Infrastructure Cost</h3>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#10b981' }}>${costEstimate.monthly_total}/mo</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', alignSelf: 'flex-end' }}>${costEstimate.annual_total}/yr</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {['compute', 'storage', 'database', 'network', 'ci_runtime'].map(key => {
                    const item = costEstimate[key];
                    if (!item) return null;
                    const pct = (item.cost / costEstimate.monthly_total) * 100;
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                        <span style={{ width: '90px', textTransform: 'capitalize' }}>{key.replace('_', ' ')}</span>
                        <div style={{ flex: 1, height: '8px', background: 'hsl(var(--secondary))', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: 'hsl(var(--accent))', borderRadius: '4px' }} />
                        </div>
                        <span style={{ fontWeight: 600, width: '50px', textAlign: 'right' }}>${item.cost}</span>
                        <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.625rem', width: '180px' }}>{item.desc}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {costEstimate && (
                <div className="card">
                  <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Cost Optimization Suggestions</h3>
                  {/* NOTE: optimizations from endpoint, using parent state */}
                </div>
              )}
            </>
          ) : <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <DollarSign size={32} style={{ color: 'hsl(var(--muted-foreground))', margin: '0 auto 0.75rem', opacity: 0.4 }} />
              <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>No cost estimate available</p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Infrastructure cost estimates appear after a successful build.</p>
            </div>}
        </div>
      )}

      {/* Compliance */}
      {tab === 'compliance' && (
        <div data-testid="compliance-panel">
          {compliance ? (
            <>
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '72px', height: '72px', borderRadius: '50%',
                      border: `4px solid ${compliance.overall_score >= 70 ? '#10b981' : '#f59e0b'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: '1.5rem', fontWeight: 800, color: compliance.overall_score >= 70 ? '#10b981' : '#f59e0b' }}>{compliance.overall_score}</span>
                    </div>
                    <div style={{ fontSize: '0.625rem', marginTop: '0.25rem', fontWeight: 600 }}>Compliance Score</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>Compliance Report</h3>
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>SOC2 alignment + GDPR readiness assessment</p>
                  </div>
                </div>

                {/* SOC2 */}
                <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>SOC2 Alignment ({compliance.soc2.passed}/{compliance.soc2.total})</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '1rem' }}>
                  {compliance.soc2.checks.map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.75rem' }}>
                      <PolicyBadge status={c.status} />
                      <span style={{ fontWeight: 600, minWidth: '160px' }}>{c.control}</span>
                      <span style={{ color: 'hsl(var(--muted-foreground))' }}>{c.detail}</span>
                    </div>
                  ))}
                </div>

                {/* GDPR */}
                <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>GDPR Readiness ({compliance.gdpr.passed}/{compliance.gdpr.total})</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '1rem' }}>
                  {compliance.gdpr.checks.map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.75rem' }}>
                      <PolicyBadge status={c.status} />
                      <span style={{ fontWeight: 600, minWidth: '140px' }}>{c.requirement}</span>
                      <span style={{ color: 'hsl(var(--muted-foreground))' }}>{c.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <Shield size={32} style={{ color: 'hsl(var(--muted-foreground))', margin: '0 auto 0.75rem', opacity: 0.4 }} />
              <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>No compliance report available</p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Compliance reports appear after a successful build.</p>
            </div>}
        </div>
      )}

      {/* Policies */}
      {tab === 'policies' && (
        <div data-testid="policies-panel">
          {policies?.policies ? (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600 }}>Policy-as-Code Controls</h3>
                <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{policies.passed}/{policies.total} passing</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {policies.policies.map((p, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 0.75rem',
                    background: 'hsl(var(--secondary))', borderRadius: '0.5rem', fontSize: '0.8125rem',
                    borderLeft: `3px solid ${p.status === 'pass' ? '#10b981' : p.status === 'fail' ? '#ef4444' : '#f59e0b'}`,
                  }}>
                    <PolicyBadge status={p.status} />
                    <span style={{ flex: 1, fontWeight: 500 }}>{p.name}</span>
                    <span style={{
                      padding: '0.125rem 0.5rem', borderRadius: '999px', fontSize: '0.5625rem', fontWeight: 600,
                      background: 'hsl(var(--background))', color: 'hsl(var(--muted-foreground))', textTransform: 'capitalize',
                    }}>{p.category}</span>
                    <span style={{
                      fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase',
                      color: p.severity === 'critical' ? '#ef4444' : p.severity === 'high' ? '#f59e0b' : p.severity === 'medium' ? '#3b82f6' : '#6b7280',
                    }}>{p.severity}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <Lock size={32} style={{ color: 'hsl(var(--muted-foreground))', margin: '0 auto 0.75rem', opacity: 0.4 }} />
              <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>No policy checks available</p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Policy-as-code checks appear after a successful build.</p>
            </div>}
        </div>
      )}
    </div>
  );
}
